import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FlexRowCenter } from '../../../components/LayoutBoxes';
import { dialogStyles as styles } from '../styles/dialogStyles';
import { BaseDialog } from './BaseDialog';

interface TelehealthInstructionsDialogProps {
  open: boolean;
  onContinue: (doNotShowAgain: boolean) => void;
  onCancel: () => void;
}

const ChromeTabSharingPreview = () => (
  <Box sx={styles.telehealthPreview} aria-hidden='true'>
    <Box sx={styles.telehealthPreviewHeader}>
      <Typography sx={styles.telehealthPreviewTitle}>
        Choose what to share
      </Typography>
      <Typography sx={styles.telehealthPreviewSubtitle}>
        The site will be able to see the contents of your screen
      </Typography>
    </Box>
    <Box sx={styles.telehealthPreviewTabs}>
      <Box sx={styles.telehealthPreviewTabActive}>Chrome tab</Box>
      <Box sx={styles.telehealthPreviewTab}>Window</Box>
      <Box sx={styles.telehealthPreviewTab}>Entire screen</Box>
    </Box>
    <Box sx={styles.telehealthPreviewBody}>
      <Box sx={styles.telehealthPreviewTabList}>
        <Box sx={styles.telehealthPreviewTabListHeader}>
          <Typography>Available tabs</Typography>
        </Box>
        <Box sx={styles.telehealthPreviewSelectedTab}>Telehealth tab</Box>
        <Box sx={styles.telehealthPreviewPlaceholder} />
        <Box sx={styles.telehealthPreviewPlaceholder} />
        <Box sx={styles.telehealthPreviewPlaceholder} />
        <Box sx={styles.telehealthPreviewPlaceholder} />
      </Box>
      <Box sx={styles.telehealthPreviewShareCard}>
        <Typography sx={styles.telehealthPreviewShareText}>
          Select a tab to share
        </Typography>
      </Box>
    </Box>
    <Box sx={styles.telehealthPreviewFooter}>
      <VolumeUpOutlinedIcon sx={styles.telehealthPreviewVolumeIcon} />
      <Typography sx={styles.telehealthPreviewShareText}>
        Also share tab audio
      </Typography>
      <Box sx={styles.telehealthPreviewToggle} />
    </Box>
  </Box>
);

export const TelehealthInstructionsDialog = ({
  open,
  onContinue,
  onCancel,
}: TelehealthInstructionsDialogProps) => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  return (
    <BaseDialog open={open} onClose={onCancel} centered portalToBody>
      <Box sx={styles.telehealthDialogContainer}>
        <Box sx={styles.telehealthPreviewContainer}>
          <ChromeTabSharingPreview />
        </Box>

        <Box sx={styles.telehealthDialogBody}>
          <Typography
            variant='dialogTitle'
            component='h2'
            sx={styles.telehealthDialogTitle}
          >
            Sharing tabs for telehealth
          </Typography>

          <Typography
            variant='dialogBody'
            component='p'
            sx={styles.telehealthDialogLead}
          >
            When you press continue, you&apos;ll see a Chrome dialog appear.
          </Typography>

          <Box component='ol' sx={styles.telehealthInstructionList}>
            <li>
              Select the tab from the list you want to use for telehealth.
            </li>
            <li>Ensure Share tab audio is turned on.</li>
            <li>Press Share.</li>
          </Box>

          <Typography sx={styles.telehealthAudioNote}>
            Scribe uses the shared tab audio to capture both sides of your
            visit. Your tab is shared only for this session.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={doNotShowAgain}
                onChange={(event) => setDoNotShowAgain(event.target.checked)}
                size='small'
                sx={styles.telehealthCheckbox}
              />
            }
            label='Do not show again'
            sx={styles.telehealthCheckboxLabel}
          />

          <FlexRowCenter sx={styles.telehealthButtonContainer}>
            <Button
              variant='outlined'
              onClick={onCancel}
              sx={styles.telehealthCancelButton}
            >
              Close
            </Button>
            <Button
              variant='contained'
              color='primary'
              startIcon={<MicNoneOutlinedIcon />}
              onClick={() => onContinue(doNotShowAgain)}
              sx={styles.telehealthContinueButton}
            >
              Continue
            </Button>
          </FlexRowCenter>
        </Box>
      </Box>
    </BaseDialog>
  );
};
